import { isCompanySettingsView, isSettingsView, viewLabels } from './view-definitions.ts';
import { sameCanvasObject } from './canvas-navigation.ts';
import type { CanvasTarget } from './types';
import type { WorkspaceView } from './view-definitions';
import { chatHash } from '../lib/chat-links.ts';
import { artifactCanvasQuery, parseArtifactCanvas } from './artifacts/routes.ts';

export {
  isCompanySettingsView,
  isSettingsView,
  settingsNavigation,
  settingsViews,
  viewDefinitions,
  viewLabels,
  workspaceNavigation,
} from './view-definitions.ts';

export const canvasViews: Record<CanvasTarget['kind'], WorkspaceView> = {
  'app-preview': 'chat',
  file: 'files',
  source: 'knowledge',
  knowledge: 'knowledge',
  skill: 'skills',
  result: 'results',
  artifact: 'results',
  'artifact-draft': 'results',
  meeting: 'meetings',
  page: 'knowledge',
  'page-proposal': 'knowledge',
  agent: 'agents',
  action: 'chat',
  'voice-preview': 'chat',
  activity: 'chat',
  web: 'chat',
  inbox: 'inbox',
  choices: 'chat',
  storefront: 'chat',
  'project-draft': 'chat',
  project: 'chat',
};

const idCanvasKinds = [
  'app-preview',
  'file',
  'source',
  'skill',
  'result',
  'meeting',
  'page',
  'page-proposal',
  'agent',
  'action',
  'activity',
  'web',
  'inbox',
  'choices',
  'storefront',
  'project-draft',
  'project',
];

const followedHows = ['picked', 'agent', 'found', 'read'];

function canvasQuery(canvas: CanvasTarget) {
  const focus =
    canvas.kind === 'page' || canvas.kind === 'page-proposal'
      ? `${canvas.section ? '&section=' + encodeURIComponent(canvas.section) : ''}${canvas.revision ? '&revision=' + encodeURIComponent(canvas.revision) : ''}${canvas.suggestion ? '&suggestion=' + encodeURIComponent(canvas.suggestion) : ''}`
      : canvas.kind === 'skill'
        ? `${canvas.revision ? '&revision=' + encodeURIComponent(canvas.revision) : ''}${canvas.followed ? '&followed=' + canvas.followed : ''}${canvas.need ? '&need=' + encodeURIComponent(canvas.need) : ''}`
        : '';
  return `?canvas=${canvas.kind}${canvas.id ? '&id=' + encodeURIComponent(canvas.id) : ''}${canvas.full ? '&full=1' : ''}${canvas.kind === 'app-preview' && canvas.pinned ? '&pin=1' : ''}${canvas.kind === 'app-preview' && canvas.editor ? '&editor=1' : ''}${canvas.runId ? '&run=' + encodeURIComponent(canvas.runId) : ''}${canvas.tab ? '&tab=' + encodeURIComponent(canvas.tab) : ''}${canvas.activityMessageId ? '&activity=' + encodeURIComponent(canvas.activityMessageId) : ''}${canvas.activityTab ? '&activityTab=' + encodeURIComponent(canvas.activityTab) : ''}${canvas.step ? '&step=' + encodeURIComponent(canvas.step) : ''}${canvas.kind === 'web' && canvas.title ? '&title=' + encodeURIComponent(canvas.title) : ''}${focus}${artifactCanvasQuery(canvas)}`;
}

// The workspace is the only product shell. Keep bookmarks and links emitted
// by the retired chat/settings surfaces useful, but do not rewrite the current
// Settings contract: account, preferences, personal memory, profile, team and
// billing still intentionally live under #/settings/*.
const legacyRouteAliases: Record<string, string> = {
  '': '#/workspace/chat',
  '#': '#/workspace/chat',
  '#/': '#/workspace/chat',
  '#/home': '#/workspace/chat',
  '#/chat': '#/workspace/chat',
  '#/workspace': '#/workspace/chat',
  '#/library': '#/workspace/results',
  '#/inbox': '#/workspace/inbox',
  '#/reviews': '#/workspace/results',
  '#/workspace/reviews': '#/workspace/results',
  '#/files': '#/workspace/files',
  '#/results': '#/workspace/results',
  '#/meetings': '#/workspace/meetings',
  '#/skills': '#/workspace/skills',
  '#/knowledge': '#/workspace/knowledge',
  '#/connections': '#/workspace/connections',
  '#/usage': '#/settings/company/usage',
  '#/settings': '#/settings/account',
  '#/settings/company': '#/settings/company/profile',
  '#/settings/company/account': '#/settings/account',
  '#/settings/company/preferences': '#/settings/preferences',
  '#/settings/company/personal-memory': '#/settings/personal-memory',
  '#/settings/knowledge': '#/workspace/knowledge',
  '#/settings/company/knowledge': '#/workspace/knowledge',
  '#/settings/connections': '#/workspace/connections',
  '#/settings/company/connections': '#/workspace/connections',
};

// Sharing review links (`?review=<id>`): the Inbox's own item links, and links
// already stored in earlier receipts and answers that point at the Library, Skills
// or Knowledge page. They open that request in the Inbox canvas, which also reads
// decided ones.
const sharingReviewPages = new Set([
  '#/workspace/inbox',
  '#/workspace/results',
  '#/workspace/files',
  '#/workspace/skills',
  '#/workspace/knowledge',
]);

function inboxReview(path: string, query: string): string | null {
  const review = sharingReviewPages.has(path) ? new URLSearchParams(query).get('review') : null;
  return review ? routeFor('inbox', { kind: 'inbox', id: `sharing:${review}` }) : null;
}

/** Return the canonical workspace hash for a known retired route. */
export function canonicalWorkspaceHash(hash: string): string | null {
  const separator = hash.indexOf('?');
  const path = separator === -1 ? hash : hash.slice(0, separator);
  const query = separator === -1 ? '' : hash.slice(separator);
  // A project used to open as a page of the Projects list; it now opens as its home, a new
  // chat in the project. Links and bookmarks to the old page land there.
  const project =
    path === '#/workspace/projects' ? new URLSearchParams(query).get('project') : null;
  if (project) return routeFor('chat', parseCanvasRoute(hash).canvas, null, undefined, project);
  const canonical = legacyRouteAliases[path];
  const review = inboxReview(canonical ?? path, query);
  if (review) return review;
  return canonical ? `${canonical}${query}` : null;
}

export function routeFor(
  view: WorkspaceView,
  canvas?: CanvasTarget | null,
  threadId?: string | null,
  library?: LibraryTab,
  project?: string,
) {
  if (isSettingsView(view))
    return `#/settings/${isCompanySettingsView(view) ? 'company/' : ''}${view}`;
  const query =
    canvas && canvas.kind !== 'voice-preview' && (canvas.id || canvas.kind === 'knowledge')
      ? canvasQuery(canvas)
      : '';
  // The Library page's Artifacts tab is a route, so reload, a link and Back keep it. It uses
  // its own key because `tab` already belongs to the canvas.
  const tab = library && view === 'results' ? `${query ? '&' : '?'}library=${library}` : '';
  // A project's home is a new chat in it, `?project=<id>`, so reload, a link and Back keep it.
  // Once that chat has a thread it is an ordinary chat link.
  const place =
    project && view === 'chat' && !threadId
      ? `${query || tab ? '&' : '?'}project=${encodeURIComponent(project)}`
      : '';
  const hash = `#/workspace/${view}${query}${tab}${place}`;
  return view === 'chat' ? chatHash(threadId, hash) : hash;
}
function routeView(key: string): WorkspaceView {
  if (key === 'reviews') return 'results';
  return Object.hasOwn(viewLabels, key) ? (key as WorkspaceView) : 'chat';
}

export type LibraryTab = 'artifacts';

export interface WorkspaceRoute {
  view: WorkspaceView;
  canvas: CanvasTarget | null;
  library?: LibraryTab;
  /** The project whose home is open: a new chat in it. */
  project?: string;
}

/** Guard only editors whose object or view would be left. */
export function leavesCanvas(current: WorkspaceRoute, next: WorkspaceRoute): boolean {
  return Boolean(current.canvas || next.canvas) && !sameCanvasObject(current.canvas, next.canvas);
}

export function leavesView(current: WorkspaceRoute, next: WorkspaceRoute): boolean {
  return current.view !== next.view;
}

export function parseWorkspaceRoute(hash: string): WorkspaceRoute {
  const route = parseCanvasRoute(hash);
  const query = hash.split('?')[1];
  const params = new URLSearchParams(query ?? '');
  const library = route.view === 'results' && params.get('library') === 'artifacts';
  const project = route.view === 'chat' && !params.has('thread') ? params.get('project') : null;
  return {
    ...route,
    ...(library ? { library: 'artifacts' as const } : {}),
    ...(project ? { project } : {}),
  };
}

function parseCanvasRoute(hash: string): WorkspaceRoute {
  const [path, query] = hash.split('?');
  const key = path.replace(/^#\/(?:workspace|settings)\/(?:company\/)?/, '');
  const view = routeView(key);
  const params = new URLSearchParams(query ?? '');
  const kind = params.get('canvas');
  const id = params.get('id');
  const artifact = parseArtifactCanvas(params);
  if (kind === 'artifact' || kind === 'artifact-draft')
    return {
      view,
      canvas: artifact
        ? {
            ...artifact,
            full: params.has('full'),
            ...(params.get('tab') ? { tab: params.get('tab')! } : {}),
            ...(params.get('activity') ? { activityMessageId: params.get('activity')! } : {}),
            ...(params.get('activityTab') === 'steps' || params.get('activityTab') === 'sources'
              ? { activityTab: params.get('activityTab') as 'steps' | 'sources' }
              : {}),
            ...(params.get('step') ? { step: params.get('step')! } : {}),
          }
        : null,
    };
  const canvas =
    kind === 'knowledge' || (id && idCanvasKinds.includes(kind ?? ''))
      ? ({
          kind,
          ...(id ? { id } : {}),
          full: params.has('full'),
          ...(kind === 'app-preview' && params.has('pin') ? { pinned: true } : {}),
          ...(kind === 'app-preview' && params.has('editor') ? { editor: true } : {}),
          ...(params.get('run') ? { runId: params.get('run')! } : {}),
          ...(params.get('tab') ? { tab: params.get('tab')! } : {}),
          ...(params.get('activity') ? { activityMessageId: params.get('activity')! } : {}),
          ...(params.get('activityTab')
            ? { activityTab: params.get('activityTab') as 'steps' | 'sources' }
            : {}),
          ...(params.get('step') ? { step: params.get('step')! } : {}),
          ...(params.get('section') ? { section: params.get('section')! } : {}),
          ...(params.get('revision') ? { revision: params.get('revision')! } : {}),
          ...(params.get('suggestion') ? { suggestion: params.get('suggestion')! } : {}),
          ...(kind === 'skill' && followedHows.includes(params.get('followed') ?? '')
            ? { followed: params.get('followed')! }
            : {}),
          ...(kind === 'skill' && params.get('need') ? { need: params.get('need')! } : {}),
          ...(kind === 'web' && params.get('title') ? { title: params.get('title')! } : {}),
        } as CanvasTarget)
      : null;
  return { view, canvas };
}

/** What a `poneva:settings-before-close` dispatch tells the Settings page. */
export interface SettingsBeforeCloseDetail {
  afterDiscard?: () => void;
  /** An in-app change to another Settings section: the page asks with its
   * section copy and the continuation pushes the new entry once it is clean. */
  section?: WorkspaceView;
  /** The browser (Back/Forward, a hash edit) already moved the history entry
   * off Settings; an in-app request has not touched the URL yet. */
  navigationAlreadyApplied?: boolean;
}

/** Navigation out of edited Settings must honor its existing discard contract. */
export function runAfterSettingsGuard(
  current: WorkspaceView,
  next: WorkspaceView,
  complete: () => void,
  options: { navigationAlreadyApplied?: boolean } = {},
) {
  // A browser move between two sections has already changed the entry; the
  // page's own hashchange listener guards it. An in-app section change has
  // not touched the URL yet, so it is guarded here and the new entry is
  // pushed only by the continuation, never before the page has decided.
  const sectionChange =
    isSettingsView(next) && next !== current && !options.navigationAlreadyApplied;
  if (
    !isSettingsView(current) ||
    (isSettingsView(next) && !sectionChange) ||
    window.dispatchEvent(
      new CustomEvent<SettingsBeforeCloseDetail>('poneva:settings-before-close', {
        cancelable: true,
        detail: {
          // Discard completes an in-app request itself. Once the browser has
          // already moved and the page pushed Settings back over that entry,
          // it returns to the entry instead and lets the re-fired hashchange
          // complete on the clean page, so the stack keeps one copy of it.
          afterDiscard: options.navigationAlreadyApplied ? () => history.back() : complete,
          ...(sectionChange ? { section: next } : {}),
          ...options,
        },
      }),
    )
  )
    complete();
}
